import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Grid,
  Typography,
} from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const UTMCreateModal = ({ open, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    phoneNumber: "",
    text: "",
    utmSource: "",
    utmCampaign: "",
    utmMedium: "",
    utmTerm: "",
    utmContent: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setFormData({
        phoneNumber: "",
        text: "",
        utmSource: "",
        utmCampaign: "",
        utmMedium: "",
        utmTerm: "",
        utmContent: "",
      });
      setErrors({});
    }
  }, [open]);

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    
    // Limpar erro ao digitar
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: "",
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.phoneNumber || formData.phoneNumber.trim() === "") {
      newErrors.phoneNumber = i18n.t("utm.generator.validation.phoneRequired");
    }

    if (!formData.utmSource || formData.utmSource.trim() === "") {
      newErrors.utmSource = i18n.t("utm.generator.validation.sourceRequired");
    }

    if (!formData.utmCampaign || formData.utmCampaign.trim() === "") {
      newErrors.utmCampaign = i18n.t("utm.generator.validation.campaignRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      // Gerar o link UTM
      const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const params = new URLSearchParams();

      if (formData.text && formData.text.trim() !== "") {
        params.append("text", formData.text);
      }
      if (formData.utmSource && formData.utmSource.trim() !== "") {
        params.append("utm_source", formData.utmSource);
      }
      if (formData.utmCampaign && formData.utmCampaign.trim() !== "") {
        params.append("utm_campaign", formData.utmCampaign);
      }
      if (formData.utmMedium && formData.utmMedium.trim() !== "") {
        params.append("utm_medium", formData.utmMedium);
      }
      if (formData.utmTerm && formData.utmTerm.trim() !== "") {
        params.append("utm_term", formData.utmTerm);
      }
      if (formData.utmContent && formData.utmContent.trim() !== "") {
        params.append("utm_content", formData.utmContent);
      }

      const generatedLink = `${baseUrl}/w/${formData.phoneNumber}?${params.toString()}`;
      
      // Chamar o serviço para salvar o link UTM
      await api.post("/utm/track", {
        phoneNumber: formData.phoneNumber,
        utmSource: formData.utmSource,
        utmCampaign: formData.utmCampaign,
        utmMedium: formData.utmMedium,
        utmTerm: formData.utmTerm,
        utmContent: formData.utmContent,
      });

      onSave({
        ...formData,
        generatedLink,
        createdAt: new Date().toISOString(),
      });
      
      onClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{i18n.t("utm.generator.title")}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {i18n.t("utm.generator.recipientData")}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label={i18n.t("utm.generator.phoneNumber")}
              variant="outlined"
              fullWidth
              value={formData.phoneNumber}
              onChange={handleChange("phoneNumber")}
              error={Boolean(errors.phoneNumber)}
              helperText={errors.phoneNumber || i18n.t("utm.generator.phoneHelper")}
              required
              placeholder="5511999999999"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label={i18n.t("utm.generator.message")}
              variant="outlined"
              fullWidth
              multiline
              rows={2}
              value={formData.text}
              onChange={handleChange("text")}
              helperText={i18n.t("utm.generator.messageHelper")}
              placeholder={i18n.t("utm.generator.messagePlaceholder")}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom style={{ marginTop: 16 }}>
              {i18n.t("utm.generator.requiredParams")}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label={i18n.t("utm.generator.source")}
              variant="outlined"
              fullWidth
              value={formData.utmSource}
              onChange={handleChange("utmSource")}
              error={Boolean(errors.utmSource)}
              helperText={errors.utmSource || i18n.t("utm.generator.sourceHelper")}
              required
              placeholder="facebook, google, instagram"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label={i18n.t("utm.generator.campaign")}
              variant="outlined"
              fullWidth
              value={formData.utmCampaign}
              onChange={handleChange("utmCampaign")}
              error={Boolean(errors.utmCampaign)}
              helperText={errors.utmCampaign || i18n.t("utm.generator.campaignHelper")}
              required
              placeholder="promo_blackfriday, lancamento_produto"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom style={{ marginTop: 16 }}>
              {i18n.t("utm.generator.optionalParams")}
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label={i18n.t("utm.generator.medium")}
              variant="outlined"
              fullWidth
              value={formData.utmMedium}
              onChange={handleChange("utmMedium")}
              helperText={i18n.t("utm.generator.mediumHelper")}
              placeholder="cpc, banner, email"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label={i18n.t("utm.generator.term")}
              variant="outlined"
              fullWidth
              value={formData.utmTerm}
              onChange={handleChange("utmTerm")}
              helperText={i18n.t("utm.generator.termHelper")}
              placeholder="palavra-chave"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label={i18n.t("utm.generator.content")}
              variant="outlined"
              fullWidth
              value={formData.utmContent}
              onChange={handleChange("utmContent")}
              helperText={i18n.t("utm.generator.contentHelper")}
              placeholder="variacao_a, botao_azul"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {i18n.t("common.cancel")}
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          {i18n.t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UTMCreateModal;